import React, { useState, useEffect } from 'react';
import { diligenceApi, dealsApi, documentsApi } from '../services/api';
import { can } from '../lib/permissions';
import { 
  FileSearch, AlertTriangle, Clock, User, Calendar, 
  ChevronDown, ChevronRight, Download, FileText, 
  MessageSquare, CheckCircle, AlertCircle, Circle,
  X, Plus, Trash2, Pencil, Save, XCircle
} from 'lucide-react';
import './DiligenceTracker.css';
import { formatDocType } from '../utils/formatDocType';



// Default category colors (fallback)
const DEFAULT_CATEGORY_COLORS = {
  'Financial': '#3b82f6',
  'Legal': '#8b5cf6',
  'Operational': '#f59e0b',
  'HR': '#ec4899',
  'IT': '#06b6d4',
  'Tax': '#10b981'
};

const STATUS_CONFIG = {
  Open: { 
    icon: Circle, 
    color: '#ef4444', 
    bgColor: '#fee2e2',
    label: 'Open'
  },
  Partial: { 
    icon: AlertCircle, 
    color: '#f59e0b', 
    bgColor: '#fef3c7',
    label: 'Partial'
  },
  Closed: { 
    icon: CheckCircle, 
    color: '#10b981', 
    bgColor: '#d1fae5',
    label: 'Closed'
  }
};

const CATEGORY_COLORS = {
  Financial: '#3b82f6',
  Legal: '#8b5cf6',
  Operational: '#f59e0b',
  HR: '#ec4899',
  IT: '#06b6d4',
  Tax: '#10b981'
};

function DiligenceTracker({ selectedDeal, orgUsers = [], onViewDocument, currentUser }) {
  const canEdit = can(currentUser, 'edit');
  const [deal, setDeal] = useState(selectedDeal);
  const [deals, setDeals] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [criticalGaps, setCriticalGaps] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [expandedItem, setExpandedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkingItem, setLinkingItem] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { message, onConfirm }
  
  // Item edit modal state
  const [editingItem, setEditingItem] = useState(null); // full item object
  const [editForm, setEditForm] = useState({ title: '', description: '', status: 'Open', assigned_to: '', due_date: '' });

  // Category edit modal state
  const [editingCategory, setEditingCategory] = useState(null); // full category object
  const [editCategoryForm, setEditCategoryForm] = useState({ name: '', color: '' });
  
  // Add item state
  const [addingToCategory, setAddingToCategory] = useState(null);
  const [addItemForm, setAddItemForm] = useState({
    code: '',
    title: '',
    description: '',
    status: 'Open',
    owner: '',
    dueDate: ''
  });
  
  // Comment state
  const [commentText, setCommentText] = useState('');
  const [addingCommentTo, setAddingCommentTo] = useState(null);
  
  // Add category state
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [addCategoryForm, setAddCategoryForm] = useState({
    name: '',
    codePrefix: '',
    color: '#6b7280'
  });
  
  // Color options for categories
  const colorOptions = [
    '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899', '#06b6d4', '#10b981',
    '#ef4444', '#f97316', '#eab308', '#84cc16', '#14b8a6', '#6366f1',
    '#a855f7', '#d946ef', '#f43f5e', '#64748b'
  ];

  // Load deals on mount
  useEffect(() => {
    fetchDeals();
    fetchDocuments();
  }, []);

  // Load tracker when deal changes
  useEffect(() => {
    if (deal) {
      fetchDiligenceTracker();
    }
  }, [deal?.id]);

  const fetchDeals = async () => {
    try {
      const response = await dealsApi.list();
      setDeals(response.data || []);
      if (!deal && response.data?.length > 0) {
        setDeal(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await documentsApi.list();
      setDocuments(response.data?.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchDiligenceTracker = async () => {
    if (!deal) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [trackerRes] = await Promise.all([
        diligenceApi.getByDeal(deal.id),
      ]);

      console.log('[DiligenceTracker] fetch result:', trackerRes.data);
      const fetchedCategories = trackerRes.data.categories || [];
      setCategories(fetchedCategories);

      // Normalize items: flatten category object → string, normalize status
      const statusMap = { pending: 'Open', in_progress: 'Partial', completed: 'Closed' };
      const normalizedItems = (trackerRes.data.items || []).map(item => ({
        ...item,
        category: item.category?.name || item.category || 'General',
        status: statusMap[item.status] || item.status || 'Open',
        linkedDocuments: item.document_ids || [],
        comments: Array.isArray(item.comments) ? item.comments : [],
      }));
      setItems(normalizedItems);
      setStats(trackerRes.data.stats || null);
      setCriticalGaps(trackerRes.data.criticalGaps || []);
      
      // Check for category filter from overview table
      const selectedCategory = localStorage.getItem('selectedDiligenceCategory');
      if (selectedCategory) {
        const categoryExists = fetchedCategories.find(c => c.name === selectedCategory);
        if (categoryExists) {
          setExpandedCategories([selectedCategory]);
        } else if (fetchedCategories.length > 0) {
          setExpandedCategories([fetchedCategories[0].name]);
        }
        localStorage.removeItem('selectedDiligenceCategory');
      } else if (expandedCategories.length === 0 && fetchedCategories.length > 0) {
        // Auto-expand first category if none expanded
        setExpandedCategories([fetchedCategories[0].name]);
      }
      
      // Show warning only if server explicitly says no documents
      if (trackerRes.data.hasDocuments === false) {
        setError('No documents uploaded. Tracker cannot evaluate coverage.');
      }
    } catch (error) {
      console.error('Error fetching diligence tracker:', error);
      setError('Failed to load information tracker');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleItem = (itemId) => {
    setExpandedItem(expandedItem === itemId ? null : itemId);
    // Clear editing state when collapsing
    if (expandedItem === itemId) {
      setEditingItem(null);
    }
  };

  // Item edit modal handlers
  const startEditing = (item, e) => {
    e.stopPropagation();
    setEditingItem(item);
    setEditForm({
      title: item.userTitle || item.title || '',
      description: item.userDescription || item.description || '',
      status: item.status || 'Open',
      assigned_to: item.assigned_to || '',
      due_date: item.due_date || item.dueDate || '',
    });
  };

  const cancelEditing = () => {
    setEditingItem(null);
    setEditForm({ title: '', description: '', status: 'Open', assigned_to: '', due_date: '' });
  };

  const saveItemEdits = async () => {
    if (!deal || !editingItem) return;
    const item = editingItem;
    const originalItem = { ...item };

    // Optimistic update
    setItems(prev => prev.map(i => i.id === item.id
      ? { ...i, title: editForm.title, description: editForm.description, status: editForm.status, assigned_to: editForm.assigned_to, due_date: editForm.due_date }
      : i
    ));
    setEditingItem(null);

    try {
      await diligenceApi.updateItem(deal.id, item.id, {
        title: editForm.title,
        description: editForm.description,
        status: editForm.status,
        assigned_to: editForm.assigned_to || null,
        due_date: editForm.due_date || null,
      });
    } catch (error) {
      console.error('Error saving item edits:', error);
      setItems(prev => prev.map(i => i.id === item.id ? originalItem : i));
      setError('Failed to save changes');
    }
  };

  // Category edit handlers
  const startEditingCategory = (category, e) => {
    e.stopPropagation();
    setEditingCategory(category);
    setEditCategoryForm({ name: category.name, color: category.color || '#3B82F6' });
  };

  const deleteComment = async (item, commentId) => {
    setItems(prev => prev.map(i =>
      i.id === item.id
        ? { ...i, comments: (i.comments || []).filter(c => c.id !== commentId) }
        : i
    ));
    try {
      const updatedComments = (item.comments || []).filter(c => c.id !== commentId);
      await diligenceApi.updateItem(deal.id, item.id, { comments: updatedComments });
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const saveCategoryEdit = async () => {
    if (!deal || !editingCategory) return;
    const original = { ...editingCategory };

    setCategories(prev => prev.map(c => c.id === editingCategory.id
      ? { ...c, name: editCategoryForm.name, color: editCategoryForm.color }
      : c
    ));
    setEditingCategory(null);

    try {
      await diligenceApi.updateCategory(deal.id, editingCategory.id, {
        name: editCategoryForm.name,
        color: editCategoryForm.color,
      });
    } catch (error) {
      console.error('Error updating category:', error);
      setCategories(prev => prev.map(c => c.id === original.id ? original : c));
      setError('Failed to update category');
    }
  };

  // Handle status change
  const handleStatusChange = async (item, newStatus, e) => {
    e?.stopPropagation();
    if (!deal) return;
    
    const originalItem = { ...item };
    
    // Optimistic update
    const updatedItems = items.map(i => {
      if (i.id === item.id) {
        return { ...i, status: newStatus, manualStatus: newStatus };
      }
      return i;
    });
    setItems(updatedItems);
    
    try {
      await diligenceApi.updateItem(deal.id, item.id, { status: newStatus });
      // Refresh to get server state
      fetchDiligenceTracker();
    } catch (error) {
      console.error('Error updating status:', error.response?.data || error.message);
      // Rollback on error
      setItems(items.map(i => i.id === item.id ? originalItem : i));
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to update status');
    }
  };

  // Add item handlers
  const startAddingItem = (category, e) => {
    e.stopPropagation();
    setAddingToCategory(category);
    setAddItemForm({
      code: '',
      title: '',
      description: '',
      status: 'Open',
      owner: '',
      dueDate: ''
    });
  };

  const cancelAddingItem = () => {
    setAddingToCategory(null);
    setAddItemForm({
      code: '',
      title: '',
      description: '',
      status: 'Open',
      owner: '',
      dueDate: ''
    });
  };

  const saveNewItem = async () => {
    if (!deal || !addingToCategory) return;
    if (!addItemForm.title.trim()) return;
    
    try {
      const cat = categories.find(c => c.name === addingToCategory);
      await diligenceApi.createItem(deal.id, {
        category_id: cat?.id || null,
        title: addItemForm.title.trim(),
        description: addItemForm.description.trim(),
        due_date: addItemForm.dueDate || null,
        assigned_to: addItemForm.owner || null,
      });

      cancelAddingItem();
      if (!expandedCategories.includes(addingToCategory)) {
        setExpandedCategories(prev => [...prev, addingToCategory]);
      }
      fetchDiligenceTracker();
    } catch (error) {
      console.error('Error creating item:', error);
      setError('Failed to create item');
    }
  };

  // Comment handlers
  const addComment = async (item) => {
    if (!deal || !commentText.trim()) return;

    const text = commentText.trim();
    const tempId = `temp-${Date.now()}`;
    const authorName = orgUsers.length > 0 ? (orgUsers[0]?.full_name || orgUsers[0]?.name || 'User') : 'User';

    // Optimistic update
    const newComment = { id: tempId, text, author: authorName, createdAt: new Date().toISOString() };
    setItems(prev => prev.map(i =>
      i.id === item.id
        ? { ...i, comments: [...(Array.isArray(i.comments) ? i.comments : []), newComment] }
        : i
    ));
    setCommentText('');

    try {
      const res = await diligenceApi.addComment(deal.id, item.id, text, authorName);
      // Replace temp comment with real one from server
      setItems(prev => prev.map(i =>
        i.id === item.id
          ? { ...i, comments: (Array.isArray(i.comments) ? i.comments : []).map(c => c.id === tempId ? res.data : c) }
          : i
      ));
    } catch (error) {
      console.error('Error adding comment:', error);
      setError('Failed to add comment');
      setItems(prev => prev.map(i =>
        i.id === item.id
          ? { ...i, comments: (i.comments || []).filter(c => c.id !== tempId) }
          : i
      ));
      // Rollback
      fetchDiligenceTracker();
    }
  };

  const handleExportCSV = async () => {
    if (!deal) return;
    
    try {
      // Export not yet implemented in API
      console.log('Would export CSV for deal:', deal.name);
      alert('CSV export coming soon!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  const handleExportPDF = async () => {
    if (!deal) return;
    
    try {
      // Export not yet implemented in API
      console.log('Would export PDF for deal:', deal.name);
      alert('PDF export coming soon!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };

  const openLinkModal = (item) => {
    setLinkingItem(item);
    setShowLinkModal(true);
  };

  const handleLinkDocument = async (documentId) => {
    if (!deal || !linkingItem) return;

    try {
      // Close modal immediately for snappy UX
      setShowLinkModal(false);
      const itemId = linkingItem.id;
      setLinkingItem(null);

      // Optimistic update
      setItems(prev => prev.map(i =>
        i.id === itemId
          ? { ...i, linkedDocuments: [...(i.linkedDocuments || []), documentId], document_ids: [...(i.document_ids || []), documentId] }
          : i
      ));

      await diligenceApi.addDocument(deal.id, itemId, documentId);
    } catch (error) {
      console.error('Error linking document:', error);
    }
  };

  const handleUnlinkDocument = (itemId, documentId) => {
    if (!deal) return;
    const docName = getDocumentName(documentId);
    setConfirmDialog({
      message: `Remove "${docName}" from this item?`,
      onConfirm: async () => {
        try {
          await diligenceApi.removeDocument(deal.id, itemId, documentId);
          setItems(prev => prev.map(i =>
            i.id === itemId
              ? { ...i, linkedDocuments: (i.linkedDocuments || []).filter(d => d !== documentId), document_ids: (i.document_ids || []).filter(d => d !== documentId) }
              : i
          ));
        } catch (error) {
          console.error('Error unlinking document:', error);
          setError('Failed to remove document link');
        }
      }
    });
  };

  const handleDeleteItem = (item, e) => {
    e.stopPropagation();
    if (!deal) return;
    setConfirmDialog({
      message: `Delete "${item.userTitle || item.title}"?`,
      onConfirm: async () => {
        try {
          await diligenceApi.deleteItem(deal.id, item.id);
          fetchDiligenceTracker();
        } catch (error) {
          console.error('Error deleting item:', error);
          setError('Failed to delete item');
        }
      }
    });
  };

  // Category handlers
  const generateCodePrefix = (name) => {
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 3).toUpperCase();
    }
    return words.slice(0, 3).map(w => w[0]).join('').toUpperCase();
  };

  const handleCreateCategory = async () => {
    if (!deal) return;
    if (!addCategoryForm.name.trim()) return;
    
    const codePrefix = addCategoryForm.codePrefix.trim() || generateCodePrefix(addCategoryForm.name);
    
    const name = addCategoryForm.name.trim();
    const color = addCategoryForm.color || '#6b7280';

    try {
      const res = await diligenceApi.createCategory(deal.id, { name, color });
      const newCategory = res.data || { id: Date.now().toString(), deal_id: deal.id, name, color };

      // Optimistic update — add to state immediately
      setCategories(prev => [...prev, newCategory]);
      setExpandedCategories(prev => [...prev, name]);
      setAddCategoryForm({ name: '', codePrefix: '', color: '#6b7280' });
      setShowAddCategory(false);
    } catch (error) {
      console.error('Error creating category:', error);
      setError(error.response?.data?.error || 'Failed to create category');
    }
  };

  const handleDeleteCategory = (category) => {
    if (!deal) return;

    const itemsInCategory = items.filter(i => i.category_id === category.id);
    const message = itemsInCategory.length > 0
      ? `Delete "${category.name}"? This category has ${itemsInCategory.length} item(s) that will also be deleted.`
      : `Delete "${category.name}"?`;

    setConfirmDialog({
      message,
      onConfirm: async () => {
        try {
          await diligenceApi.deleteCategory(deal.id, category.id);
          fetchDiligenceTracker();
        } catch (error) {
          console.error('Error deleting category:', error);
          setError(error.response?.data?.error || 'Failed to delete category');
        }
      }
    });
  };

  const getCategoryColor = (category) => {
    return category.color || DEFAULT_CATEGORY_COLORS[category.name] || '#6b7280';
  };

  const getCategoryItems = (categoryId) => {
    return items.filter(item => item.category_id === categoryId);
  };

  const getCategoryStats = (categoryId) => {
    const catItems = getCategoryItems(categoryId);
    const total = catItems.length;
    const closed = catItems.filter(i => i.status === 'Closed').length;
    const completionPct = total === 0 ? 0 : Math.round((closed / total) * 100);
    return { total, closed, completionPct };
  };

  const getDocumentName = (docId) => {
    const doc = documents.find(d => d.jobId === docId);
    return doc?.filename || docId;
  };

  const getDocumentStatus = (docId) => {
    const doc = documents.find(d => d.jobId === docId);
    return doc?.parseStatus?.status;
  };

  // Get display title/description (user override or default)
  const getDisplayTitle = (item) => item.userTitle || item.title;
  const getDisplayDescription = (item) => item.userDescription || item.description;
  const getUserName = (userId) => {
    if (!userId) return null;
    const u = orgUsers.find(u => u.id === userId);
    return u ? (u.full_name || u.name || u.email) : null;
  };

  if (loading) {
    return (
      <div className="deal-tab-panel">
        <div className="dt-loading">
          <div className="dt-spinner"></div>
          <p>Loading information tracker...</p>
        </div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="deal-tab-panel">
        <div className="dt-empty">
          <FileSearch size={48} />
          <h3>No deal selected</h3>
          <p>Select a deal to view its information tracker</p>
        </div>
      </div>
    );
  }

  return (
    <div className="deal-tab-panel">
      <div className="deal-tab-header">
        <h3>IRL ({items.length})</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {canEdit && <button className="deal-btn-add" onClick={() => setShowAddCategory(true)}>
            <Plus size={16} />Add Category
          </button>}
        </div>
      </div>

      {/* Error/Warning Banner */}
      {error && (
        <div className={`dt-banner ${error.includes('No documents') ? 'dt-banner-warning' : 'dt-banner-error'}`}>
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Critical Gaps Alert */}
      {criticalGaps.length > 0 && (
        <div className="dt-critical-alert">
          <AlertTriangle size={20} />
          <div>
            <strong>Critical information gaps detected</strong>
            <p>Missing: {criticalGaps.map(g => g.title).join(', ')}</p>
          </div>
        </div>
      )}

      {/* Stats Summary */}
      {stats && (
        <div className="dt-stats">
          <div className="dt-stat-card">
            <div className="dt-stat-value" style={{ color: '#161f4a' }}>
              {stats.completionPct}%
            </div>
            <div className="dt-stat-label">Complete</div>
          </div>
          <div className="dt-stat-card">
            <div className="dt-stat-value" style={{ color: '#10b981' }}>
              {stats.closed}
            </div>
            <div className="dt-stat-label">Closed</div>
          </div>
          <div className="dt-stat-card">
            <div className="dt-stat-value" style={{ color: '#f59e0b' }}>
              {stats.partial}
            </div>
            <div className="dt-stat-label">Partial</div>
          </div>
          <div className="dt-stat-card">
            <div className="dt-stat-value" style={{ color: '#ef4444' }}>
              {stats.open}
            </div>
            <div className="dt-stat-label">Open</div>
          </div>
          <div className="dt-stat-card">
            <div className="dt-stat-value">{stats.total}</div>
            <div className="dt-stat-label">Total Items</div>
          </div>
        </div>
      )}

      {/* Add Category Form */}
      {showAddCategory && canEdit && (
        <div className="dt-add-category-form">
          <input
            type="text"
            placeholder="Category Name *"
            value={addCategoryForm.name}
            onChange={(e) => {
              const name = e.target.value;
              setAddCategoryForm({
                ...addCategoryForm,
                name,
                codePrefix: addCategoryForm.codePrefix || generateCodePrefix(name)
              });
            }}
            className="dt-input"
            autoFocus
          />
          <div className="dt-color-picker">
            {colorOptions.map(color => (
              <button
                key={color}
                type="button"
                className={`dt-color-option ${addCategoryForm.color === color ? 'active' : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => setAddCategoryForm({...addCategoryForm, color})}
              />
            ))}
          </div>
          <div className="dt-add-category-actions">
            <button
              className="dt-btn-sm dt-btn-primary"
              onClick={handleCreateCategory}
              disabled={!addCategoryForm.name.trim()}
            >
              <Save size={14} /> Save
            </button>
            <button
              className="dt-btn-sm dt-btn-secondary"
              onClick={() => {
                setShowAddCategory(false);
                setAddCategoryForm({ name: '', codePrefix: '', color: '#6b7280' });
              }}
            >
              <XCircle size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Category Accordions */}
      <div className="dt-categories">
        {categories.map(category => {
          const categoryItems = getCategoryItems(category.id);
          const categoryStats = getCategoryStats(category.id);
          const isExpanded = expandedCategories.includes(category.name);
          
          // Always show all categories
          
          return (
            <div key={category.id} className="dt-category">
              <div
                className="dt-category-header"
                onClick={() => toggleCategory(category.name)}
                style={{ borderLeftColor: getCategoryColor(category), cursor: 'pointer' }}
              >
                <div className="dt-category-title">
                  {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  <span>{category.name}</span>
                  <span className="dt-category-count">({categoryItems.length})</span>
                  {canEdit && <span
                    className="dt-btn-delete-category"
                    onClick={(e) => startEditingCategory(category, e)}
                    title="Edit category"
                    role="button"
                  >
                    <Pencil size={12} />
                  </span>}
                  {canEdit && <span
                    className="dt-btn-delete-category"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCategory(category);
                    }}
                    title="Delete category"
                    role="button"
                  >
                    <Trash2 size={12} />
                  </span>}
                </div>
                <div className="dt-category-progress">
                  <div className="dt-progress-bar">
                    <div
                      className="dt-progress-fill"
                      style={{
                        width: `${categoryStats.completionPct}%`,
                        backgroundColor: getCategoryColor(category)
                      }}
                    />
                  </div>
                  <span className="dt-progress-text">{categoryStats.completionPct}%</span>
                </div>
              </div>
              
              {isExpanded && (
                <div className="dt-category-content">
                  {categoryItems.map(item => {
                    const statusConfig = STATUS_CONFIG[item.status];
                    const StatusIcon = statusConfig.icon;
                    const isItemExpanded = expandedItem === item.id;
                    const isEditing = editingItem === item.id;
                    
                    return (
                      <div 
                        key={item.id} 
                        className={`dt-item ${isItemExpanded ? 'dt-item-expanded' : ''}`}
                      >
                        <div
                          className="dt-item-header"
                          onClick={() => toggleItem(item.id)}
                        >
                          <span className="dt-item-chevron">
                            {isItemExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </span>
                          <div className="dt-item-title-section">
                            <div className="dt-item-title">
                              {getDisplayTitle(item)}
                              {canEdit && <button
                                className="dt-btn-edit"
                                onClick={(e) => startEditing(item, e)}
                                title="Edit"
                              >
                                <Pencil size={12} />
                              </button>}
                              {canEdit && <button
                                className="dt-btn-delete"
                                onClick={(e) => handleDeleteItem(item, e)}
                                title="Delete"
                              >
                                <Trash2 size={12} />
                              </button>}
                            </div>
                            <div className="dt-item-meta">
                              {getUserName(item.assigned_to) && (
                                <span className="dt-item-owner">
                                  <User size={12} />
                                  {getUserName(item.assigned_to)}
                                </span>
                              )}
                              {(item.linkedDocuments?.length > 0 || item.document_ids?.length > 0) && (
                                <span className="dt-item-doc-count">
                                  <FileText size={12} />
                                  {(item.linkedDocuments?.length || item.document_ids?.length || 0)} doc{(item.linkedDocuments?.length || item.document_ids?.length || 0) > 1 ? 's' : ''}
                                </span>
                              )}
                              {item.parseError && (
                                <span className="dt-item-parse-error">
                                  <AlertTriangle size={12} />
                                  Parse error
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="dt-item-status">
                            <select
                              className="dt-status-select"
                              value={item.status}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => canEdit && handleStatusChange(item, e.target.value, e)}
                              disabled={!canEdit}
                              style={{
                                backgroundColor: statusConfig.bgColor,
                                color: statusConfig.color
                              }}
                            >
                              <option value="Open" style={{ color: '#ef4444' }}>Open</option>
                              <option value="Partial" style={{ color: '#f59e0b' }}>Partial</option>
                              <option value="Closed" style={{ color: '#10b981' }}>Closed</option>
                            </select>
                          </div>
                        </div>
                        
                        {isItemExpanded && (
                          <div className="dt-item-details">
                            <div className="dt-details-layout">

                              {/* LEFT: sidebar — metadata + docs */}
                              <div className="dt-details-sidebar">

                                {/* Metadata */}
                                <div className="dt-meta-block">
                                  <div className="dt-meta-row">
                                    <span className="dt-meta-label"><User size={12} /> Owner</span>
                                    <select
                                      className="dt-meta-select"
                                      value={item.assigned_to || ''}
                                      onChange={(e) => {
                                        if (!canEdit) return;
                                        diligenceApi.updateItem(deal.id, item.id, { assigned_to: e.target.value || null });
                                        setItems(prev => prev.map(i => i.id === item.id ? { ...i, assigned_to: e.target.value } : i));
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      disabled={!canEdit}
                                    >
                                      <option value="">Unassigned</option>
                                      {orgUsers.map(u => (
                                        <option key={u.id} value={u.id}>{u.full_name || u.name || u.email}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="dt-meta-row">
                                    <span className="dt-meta-label"><Calendar size={12} /> Due Date</span>
                                    <input
                                      className="dt-meta-input"
                                      type="date"
                                      value={item.due_date || item.dueDate || ''}
                                      disabled={!canEdit}
                                      onChange={(e) => {
                                        diligenceApi.updateItem(deal.id, item.id, { due_date: e.target.value });
                                        setItems(prev => prev.map(i => i.id === item.id ? { ...i, due_date: e.target.value } : i));
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                </div>

                                {/* Linked Documents */}
                                <div className="dt-docs-block">
                                  <span className="dt-sidebar-label"><FileText size={12} /> Documents</span>
                                  <div className="dt-doc-chips">
                                    {(item.linkedDocuments || []).map(docId => {
                                      const doc = documents.find(d => d.jobId === docId);
                                      const hasParseError = getDocumentStatus(docId) === 'failed';
                                      return (
                                        <div key={docId} className={`dt-doc-chip${hasParseError ? ' dt-doc-chip-error' : ''}`}>
                                          <FileText size={11} />
                                          <span
                                            className="dt-doc-chip-name"
                                            onClick={(e) => { e.stopPropagation(); doc && onViewDocument?.(doc); }}
                                          >{getDocumentName(docId)}</span>
                                          {canEdit && <button
                                            type="button"
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleUnlinkDocument(item.id, docId); }}
                                          >
                                            <X size={10} />
                                          </button>}
                                        </div>
                                      );
                                    })}
                                    {canEdit && <button
                                      className="dt-doc-chip-add"
                                      onClick={(e) => { e.stopPropagation(); openLinkModal(item); }}
                                    >
                                      <Plus size={11} /> Link doc
                                    </button>}
                                  </div>
                                </div>
                              </div>

                              {/* RIGHT: description + comments */}
                              <div className="dt-details-main">
                                {getDisplayDescription(item) && (
                                  <p className="dt-item-description">{getDisplayDescription(item)}</p>
                                )}

                                {/* Comments */}
                                <div className="dt-comments-area" onClick={(e) => e.stopPropagation()}>
                                  {(Array.isArray(item.comments) ? item.comments : []).length > 0 && (
                                    <div
                                      className="dt-comments-scroll-wrap"
                                      ref={el => {
                                        if (el) {
                                          const list = el.querySelector('.dt-comments-list');
                                          if (list) {
                                            const check = () => {
                                              const atBottom = list.scrollHeight - list.scrollTop <= list.clientHeight + 2;
                                              el.classList.toggle('at-bottom', atBottom);
                                            };
                                            list.addEventListener('scroll', check, { passive: true });
                                            check();
                                          }
                                        }
                                      }}
                                    >
                                      <div className="dt-comments-list">
                                        {[...(Array.isArray(item.comments) ? item.comments : [])]
                                          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                          .map(comment => (
                                            <div key={comment.id} className="dt-comment">
                                              <div className="dt-comment-header">
                                                <div className="dt-comment-meta">
                                                  <span className="dt-comment-author">{comment.author}</span>
                                                  <span className="dt-comment-time">
                                                    {new Date(comment.createdAt).toLocaleDateString()} {new Date(comment.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                  </span>
                                                </div>
                                                {canEdit && <button
                                                  className="dt-comment-delete"
                                                  onClick={(e) => { e.stopPropagation(); deleteComment(item, comment.id); }}
                                                  title="Delete comment"
                                                >
                                                  <X size={11} />
                                                </button>}
                                              </div>
                                              <p className="dt-comment-text">{comment.text}</p>
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  )}
                                  {canEdit && <div className="dt-add-comment">
                                    <textarea
                                      placeholder="Add a comment..."
                                      value={commentText}
                                      onChange={(e) => setCommentText(e.target.value)}
                                      className="dt-comment-input"
                                      rows={2}
                                    />
                                    <button
                                      className="dt-btn-sm dt-btn-primary"
                                      onClick={() => addComment(item)}
                                      disabled={!commentText.trim()}
                                    >
                                      Add Comment
                                    </button>
                                  </div>}
                                </div>
                              </div>

                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Add Item Form / Button — after items list */}
                  {canEdit && addingToCategory === category.name ? (
                    <div className="dt-add-item-header">
                      <div className="dt-add-item-form">
                        <div className="dt-add-item-row">
                          <input
                            type="text"
                            placeholder="Code (optional)"
                            value={addItemForm.code}
                            onChange={(e) => setAddItemForm({...addItemForm, code: e.target.value})}
                            className="dt-input-code"
                          />
                          <input
                            type="text"
                            placeholder="Title *"
                            value={addItemForm.title}
                            onChange={(e) => setAddItemForm({...addItemForm, title: e.target.value})}
                            className="dt-input-title"
                          />
                        </div>
                        <textarea
                          placeholder="Description (optional)"
                          value={addItemForm.description}
                          onChange={(e) => setAddItemForm({...addItemForm, description: e.target.value})}
                          className="dt-input-description"
                          rows={2}
                        />
                        <div className="dt-add-item-row">
                          <select
                            value={addItemForm.status}
                            onChange={(e) => setAddItemForm({...addItemForm, status: e.target.value})}
                            className="dt-input-status"
                          >
                            <option value="Open">Open</option>
                            <option value="Partial">Partial</option>
                            <option value="Closed">Closed</option>
                          </select>
                          <select
                            value={addItemForm.owner}
                            onChange={(e) => setAddItemForm({...addItemForm, owner: e.target.value})}
                            className="dt-input-owner"
                          >
                            <option value="">Owner</option>
                            {orgUsers.map(u => (
                              <option key={u.id} value={u.id}>
                                {u.full_name || u.name || u.email}
                              </option>
                            ))}
                          </select>
                          <input
                            type="date"
                            value={addItemForm.dueDate}
                            onChange={(e) => setAddItemForm({...addItemForm, dueDate: e.target.value})}
                            className="dt-input-date"
                          />
                        </div>
                        <div className="dt-add-item-actions">
                          <button
                            className="dt-btn-sm dt-btn-primary"
                            onClick={saveNewItem}
                            disabled={!addItemForm.title.trim()}
                          >
                            <Save size={14} /> Save
                          </button>
                          <button
                            className="dt-btn-sm dt-btn-secondary"
                            onClick={cancelAddingItem}
                          >
                            <XCircle size={14} /> Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : canEdit ? (
                    <div className="dt-add-item-footer">
                      <button
                        className="dt-btn-add-item"
                        onClick={(e) => startAddingItem(category.name, e)}
                      >
                        <Plus size={14} /> Add Item
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Link Document Modal */}
      {showLinkModal && linkingItem && (
        <div className="dt-modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="dt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dt-modal-header">
              <h3>Link Document to {linkingItem.code}</h3>
              <button className="dt-modal-close" onClick={() => setShowLinkModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="dt-modal-body">
              <p>Select a document to link to <strong>{getDisplayTitle(linkingItem)}</strong></p>
              <div className="dt-doc-list">
                {documents
                  .filter(d => !(linkingItem.linkedDocuments || []).includes(d.jobId))
                  .map(doc => (
                    <button
                      key={doc.jobId}
                      className="dt-doc-option"
                      onClick={() => handleLinkDocument(doc.jobId)}
                    >
                      <FileText size={16} />
                      <div className="dt-doc-option-info">
                        <span className="dt-doc-name">{doc.filename}</span>
                        <span className="dt-doc-type">{formatDocType(doc.documentType)}</span>
                      </div>
                      {doc.parseStatus?.status === 'failed' && (
                        <span className="dt-doc-parse-error">Parse failed</span>
                      )}
                    </button>
                  ))}
                {documents.filter(d => !(linkingItem.linkedDocuments || []).includes(d.jobId)).length === 0 && (
                  <p className="dt-empty-state">No available documents to link</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item Edit Modal */}
      {editingItem && (
        <div className="dt-modal-overlay" onClick={cancelEditing}>
          <div className="dt-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dt-modal-header">
              <h3>Edit Item</h3>
              <button className="dt-modal-close" onClick={cancelEditing}><X size={20} /></button>
            </div>
            <div className="dt-edit-modal-body">
              <div className="dt-edit-modal-field">
                <label>Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="Item title"
                  autoFocus
                />
              </div>
              <div className="dt-edit-modal-field">
                <label>Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Add a description..."
                  rows={3}
                />
              </div>
              <div className="dt-edit-modal-row">
                <div className="dt-edit-modal-field">
                  <label>Status</label>
                  <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                    <option value="Open">Open</option>
                    <option value="Partial">Partial</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
                <div className="dt-edit-modal-field">
                  <label>Owner</label>
                  <select value={editForm.assigned_to} onChange={(e) => setEditForm({ ...editForm, assigned_to: e.target.value })}>
                    <option value="">Unassigned</option>
                    {orgUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.full_name || u.name || u.email}</option>
                    ))}
                  </select>
                </div>
                <div className="dt-edit-modal-field">
                  <label>Due Date</label>
                  <input type="date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="dt-edit-modal-footer">
              <button className="dt-btn-secondary" onClick={cancelEditing}>Cancel</button>
              <button className="dt-btn-primary" onClick={saveItemEdits} disabled={!editForm.title.trim()}>
                <Save size={14} /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Edit Modal */}
      {editingCategory && (
        <div className="dt-modal-overlay" onClick={() => setEditingCategory(null)}>
          <div className="dt-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dt-modal-header" style={{ padding: '0 0 16px 0', border: 'none' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Edit Category</h3>
              <button className="dt-modal-close" onClick={() => setEditingCategory(null)}><X size={18} /></button>
            </div>
            <div className="dt-edit-modal-field" style={{ marginBottom: 16 }}>
              <label>Name</label>
              <input
                type="text"
                value={editCategoryForm.name}
                onChange={(e) => setEditCategoryForm({ ...editCategoryForm, name: e.target.value })}
                autoFocus
              />
            </div>
            <div className="dt-edit-modal-field" style={{ marginBottom: 20 }}>
              <label>Color</label>
              <div className="dt-color-swatches">
                {['#3B82F6','#8B5CF6','#10B981','#F59E0B','#EF4444','#EC4899','#6366F1','#14B8A6','#F97316','#6b7280'].map(c => (
                  <button
                    key={c}
                    className={`dt-color-swatch${editCategoryForm.color === c ? ' active' : ''}`}
                    style={{ background: c }}
                    onClick={() => setEditCategoryForm({ ...editCategoryForm, color: c })}
                  />
                ))}
              </div>
            </div>
            <div className="dt-confirm-actions">
              <button className="dt-btn-secondary" onClick={() => setEditingCategory(null)}>Cancel</button>
              <button className="dt-btn-primary" onClick={saveCategoryEdit} disabled={!editCategoryForm.name.trim()}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog && (
        <div className="dt-modal-overlay" onClick={() => setConfirmDialog(null)}>
          <div className="dt-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <p>{confirmDialog.message}</p>
            <div className="dt-confirm-actions">
              <button
                className="dt-btn-sm dt-btn-danger"
                onClick={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }}
              >
                Delete
              </button>
              <button
                className="dt-btn-sm dt-btn-secondary"
                onClick={() => setConfirmDialog(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DiligenceTracker;
